import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Loader2, ArrowLeft, Download, XCircle, Save } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { generateVoucherPDF, type VoucherData } from "@/lib/partner/voucher";

export const Route = createFileRoute("/partner/orders/$orderId")({
  head: () => ({
    meta: [
      { title: "Order — Bacha Way Travel" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: OrderDetail,
});

interface OrderFull {
  order_id: string;
  status: string;
  start_date_time: string;
  passenger_name: string;
  passenger_phone: string;
  passenger_email: string | null;
  passenger_count: number;
  flight_number: string | null;
  meeting_sign_text: string | null;
  partner_comment: string | null;
  price_amount: number;
  price_currency: string;
  price_locked_until: string;
  free_cancel_until: string | null;
  penalty_amount: number;
  supplier_link: string | null;
  driver_name: string | null;
  driver_phone: string | null;
  car_plate: string | null;
  car_model_en: string | null;
  car_color_en: string | null;
  cancelled_at: string | null;
  offer_id: string | null;
}

function OrderDetail() {
  const { orderId } = Route.useParams();
  const { user, isAuthenticated, loading } = useAuth();
  const navigate = useNavigate();
  const [order, setOrder] = useState<OrderFull | null>(null);
  const [pickupLabel, setPickupLabel] = useState<string>("");
  const [dropoffLabel, setDropoffLabel] = useState<string>("");
  const [busy, setBusy] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);

  useEffect(() => {
    if (!loading && !isAuthenticated) navigate({ to: "/login", replace: true });
  }, [loading, isAuthenticated, navigate]);

  useEffect(() => {
    if (!user) return;
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, orderId]);

  async function load() {
    setBusy(true);
    const { data } = await supabase
      .from("bookings_orders")
      .select("*")
      .eq("order_id", orderId)
      .maybeSingle();
    if (data) {
      setOrder(data as OrderFull);
      if ((data as OrderFull).offer_id) {
        const { data: o } = await supabase
          .from("transfers_offers")
          .select("from_label, to_label")
          .eq("id", (data as OrderFull).offer_id!)
          .maybeSingle();
        if (o) {
          setPickupLabel(o.from_label);
          setDropoffLabel(o.to_label);
        }
      }
    }
    setBusy(false);
  }

  async function saveModifications() {
    if (!order) return;
    setSaving(true);
    setMsg(null);
    const { error } = await supabase
      .from("bookings_orders")
      .update({
        passenger_name: order.passenger_name,
        passenger_phone: order.passenger_phone,
        passenger_email: order.passenger_email,
        flight_number: order.flight_number,
        meeting_sign_text: order.meeting_sign_text,
        partner_comment: order.partner_comment,
        driver_name: order.driver_name,
        driver_phone: order.driver_phone,
        car_model_en: order.car_model_en,
        car_color_en: order.car_color_en,
        car_plate: order.car_plate,
        status: order.status,
      })
      .eq("order_id", order.order_id);
    setSaving(false);
    setMsg(error ? `Error: ${error.message}` : "Saved.");
  }

  async function cancelOrder() {
    if (!order) return;
    if (!confirm("Cancel this order? A penalty may apply per the cancellation policy.")) return;
    const reason = prompt("Cancellation reason (optional):") || undefined;

    const now = Date.now();
    const startMs = new Date(order.start_date_time).getTime();
    const freeUntil = order.free_cancel_until ? new Date(order.free_cancel_until).getTime() : startMs;
    const hours = (startMs - now) / 3_600_000;
    const price = Number(order.price_amount);
    let penalty = 0;
    if (now >= freeUntil) penalty = hours <= 2 ? price : Math.round(price * 0.5 * 100) / 100;

    const { error } = await supabase
      .from("bookings_orders")
      .update({
        status: "cancelled",
        cancelled_at: new Date().toISOString(),
        penalty_amount: penalty,
        partner_comment: reason
          ? `${order.partner_comment ?? ""}\n[CANCEL] ${reason}`.trim()
          : order.partner_comment,
      })
      .eq("order_id", order.order_id);
    if (error) setMsg(`Error: ${error.message}`);
    else void load();
  }

  function downloadVoucher() {
    if (!order) return;
    const v: VoucherData = {
      order_id: order.order_id,
      status: order.status,
      passenger_name: order.passenger_name,
      passenger_phone: order.passenger_phone,
      passenger_email: order.passenger_email,
      passenger_count: order.passenger_count,
      start_date_time: order.start_date_time,
      flight_number: order.flight_number,
      meeting_sign_text: order.meeting_sign_text,
      pickup_label: pickupLabel,
      dropoff_label: dropoffLabel,
      car_model: order.car_model_en,
      driver_name: order.driver_name,
      driver_phone: order.driver_phone,
      car_plate: order.car_plate,
      price_amount: Number(order.price_amount),
      price_currency: order.price_currency,
      partner_comment: order.partner_comment,
    };
    const doc = generateVoucherPDF(v);
    doc.save(`voucher-${order.order_id}.pdf`);
  }

  if (loading || busy || !order) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  const set = <K extends keyof OrderFull>(k: K, v: OrderFull[K]) =>
    setOrder({ ...order, [k]: v });

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b">
        <div className="container max-w-4xl mx-auto px-4 py-5 flex items-center justify-between">
          <Link to="/partner" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground">
            <ArrowLeft className="h-4 w-4" /> Back to orders
          </Link>
          <div className="flex gap-2">
            <button
              onClick={downloadVoucher}
              className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-md border hover:bg-muted"
            >
              <Download className="h-4 w-4" /> Voucher PDF
            </button>
            {order.status !== "cancelled" && order.status !== "completed" && (
              <button
                onClick={cancelOrder}
                className="inline-flex items-center gap-2 px-3 py-2 text-sm rounded-md border border-red-300 text-red-700 hover:bg-red-50"
              >
                <XCircle className="h-4 w-4" /> Cancel
              </button>
            )}
          </div>
        </div>
      </header>

      <main className="container max-w-4xl mx-auto px-4 py-8 space-y-6">
        <div>
          <h1 className="text-2xl font-serif text-primary">Order #{order.order_id}</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Status: <strong>{order.status}</strong> • Pickup:{" "}
            {new Date(order.start_date_time).toLocaleString()} •{" "}
            {Number(order.price_amount).toFixed(2)} {order.price_currency}
            {order.penalty_amount > 0 && (
              <> • Penalty: {Number(order.penalty_amount).toFixed(2)} {order.price_currency}</>
            )}
          </p>
        </div>

        {msg && (
          <div className="rounded border bg-muted px-4 py-2 text-sm">{msg}</div>
        )}

        <Section title="Itinerary">
          <Field label="From">{pickupLabel || "—"}</Field>
          <Field label="To">{dropoffLabel || "—"}</Field>
          <Field label="Free cancellation until">
            {order.free_cancel_until ? new Date(order.free_cancel_until).toLocaleString() : "—"}
          </Field>
        </Section>

        <Section title="Passenger">
          <Input label="Lead passenger" value={order.passenger_name} onChange={(v) => set("passenger_name", v)} />
          <Input label="Phone" value={order.passenger_phone} onChange={(v) => set("passenger_phone", v)} />
          <Input label="Email" value={order.passenger_email ?? ""} onChange={(v) => set("passenger_email", v || null)} />
          <Input label="Flight number" value={order.flight_number ?? ""} onChange={(v) => set("flight_number", v || null)} />
          <Input label="Meeting sign" value={order.meeting_sign_text ?? ""} onChange={(v) => set("meeting_sign_text", v || null)} />
        </Section>

        <Section title="Driver & Vehicle (English / Latin script only)">
          <Input label="Driver name" value={order.driver_name ?? ""} onChange={(v) => set("driver_name", v || null)} />
          <Input label="Driver phone" value={order.driver_phone ?? ""} onChange={(v) => set("driver_phone", v || null)} />
          <Input label="Car model" value={order.car_model_en ?? ""} onChange={(v) => set("car_model_en", v || null)} />
          <Input label="Car color" value={order.car_color_en ?? ""} onChange={(v) => set("car_color_en", v || null)} />
          <Input label="License plate" value={order.car_plate ?? ""} onChange={(v) => set("car_plate", v || null)} />
        </Section>

        <Section title="Status & notes">
          <div className="space-y-2">
            <label className="text-xs uppercase tracking-wide text-muted-foreground">Status</label>
            <select
              value={order.status}
              onChange={(e) => set("status", e.target.value)}
              className="w-full rounded-md border px-3 py-2 bg-background"
            >
              <option value="pending">pending</option>
              <option value="confirmed">confirmed</option>
              <option value="completed">completed</option>
              <option value="cancelled">cancelled</option>
            </select>
          </div>
          <div className="space-y-2 md:col-span-2">
            <label className="text-xs uppercase tracking-wide text-muted-foreground">Partner comment</label>
            <textarea
              value={order.partner_comment ?? ""}
              onChange={(e) => set("partner_comment", e.target.value || null)}
              rows={3}
              className="w-full rounded-md border px-3 py-2 bg-background"
            />
          </div>
        </Section>

        <div className="flex justify-end">
          <button
            onClick={saveModifications}
            disabled={saving}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-primary text-primary-foreground hover:opacity-90 disabled:opacity-50"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            Save changes
          </button>
        </div>
      </main>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="rounded-lg border bg-card p-5">
      <h2 className="text-lg font-semibold text-primary mb-4">{title}</h2>
      <div className="grid md:grid-cols-2 gap-4">{children}</div>
    </section>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-xs uppercase tracking-wide text-muted-foreground">{label}</div>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function Input({
  label,
  value,
  onChange,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div className="space-y-2">
      <label className="text-xs uppercase tracking-wide text-muted-foreground">{label}</label>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded-md border px-3 py-2 bg-background"
      />
    </div>
  );
}
